/**
 * WB3-P2-6 — Command palette focus bridge (BusinessWorkspaceState → resolver)
 * WB4 LLM-PALETTE — operational NL + suggest API augmentation merged into grouped commands.
 */

import { useMemo } from 'react';
import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import type { WorkbenchCommandContext } from '@shared/business-workbench/command-resolver';
import type { UniversalBaseEntity } from '@shared/types/entity';
import { mergePaletteLlmIntoGrouped } from '@shared/business-workbench/workbench-palette-llm-resolver';
import { useOptionalDerivedWorkbenchFocus } from './useOptionalDerivedWorkbenchFocus';
import {
  useResolvedWorkbenchCommands,
  type UseResolvedWorkbenchCommandsOptions,
} from './useResolvedWorkbenchCommands';
import { type WorkbenchPaletteCommands } from '../../utils/globalSearchCommands';
import { useWorkbenchPermissionContext } from './useWorkbenchPermissionContext';
import { usePaletteLlmAugmentation } from '../usePaletteLlmAugmentation';
import { usePaletteIntentProposal } from '../usePaletteIntentProposal';
import type { PaletteIntentProposal } from '@shared/business-workbench/workbench-palette-intent-proposal';

export function resolveActiveObjectRef(
  root: ObjectRef,
  selection: readonly ObjectRef[]
): ObjectRef | undefined {
  const lastSelected = selection.length > 0 ? selection[selection.length - 1] : undefined;
  if (lastSelected?.id) {
    return lastSelected;
  }
  if (root.id) {
    return root;
  }
  return undefined;
}

export function objectRefToCommandEntity(
  ref: ObjectRef,
  tenantId = 'default_tenant'
): UniversalBaseEntity {
  const now = new Date(0);
  return {
    id: ref.id,
    name: ref.label ?? ref.id,
    tenantId,
    status: 'active',
    businessDomain: ref.businessDomain,
    createdAt: now,
    updatedAt: now,
  };
}

export interface UseWorkbenchCommandContextOptions {
  query?: string;
  tenantId?: string;
  /** When false, skips debounced /api/ai/suggest augmentation. */
  llmAugmentationEnabled?: boolean;
}

export interface WorkbenchCommandContextResult extends WorkbenchPaletteCommands {
  hasObjectContext: boolean;
  rootLabel?: string;
  businessDomain?: string;
  commandObject?: UniversalBaseEntity;
  tenantId?: string;
  llmLoading?: boolean;
  llmStub?: boolean;
  intentProposal?: PaletteIntentProposal;
  commandContext: WorkbenchCommandContext;
}

export function useWorkbenchCommandContext(
  options: UseWorkbenchCommandContextOptions = {}
): WorkbenchCommandContextResult {
  const activeFocus = useOptionalDerivedWorkbenchFocus({ actionProjection: 'commandPalette' });
  const query = options.query ?? '';
  const tenantId = options.tenantId ?? 'default_tenant';
  const { role, plane, permissionKeys } = useWorkbenchPermissionContext();

  const objectContext = useMemo(() => {
    if (!activeFocus) {
      return null;
    }

    const activeRef = resolveActiveObjectRef(activeFocus.root, activeFocus.selection);
    if (!activeRef) {
      return null;
    }

    return {
      object: objectRefToCommandEntity(activeRef, tenantId),
      businessDomain: activeRef.businessDomain,
      status: 'active',
      rootLabel: activeRef.label ?? activeRef.id,
      focus: activeFocus,
    };
  }, [activeFocus, tenantId]);

  const resolvedOptions: UseResolvedWorkbenchCommandsOptions = useMemo(() => {
    if (!objectContext) {
      return { query };
    }
    return {
      query,
      object: objectContext.object,
      businessDomain: objectContext.businessDomain,
      status: objectContext.status,
      focus: objectContext.focus,
    };
  }, [query, objectContext]);

  const baseCommands = useResolvedWorkbenchCommands(resolvedOptions);

  const resolverCtx: WorkbenchCommandContext = useMemo(
    () => ({
      role,
      plane,
      permissionKeys,
      ...(objectContext?.object !== undefined ? { object: objectContext.object } : {}),
      ...(objectContext?.businessDomain !== undefined
        ? { businessDomain: objectContext.businessDomain }
        : {}),
      ...(objectContext?.status !== undefined ? { status: objectContext.status } : {}),
      ...(objectContext?.focus !== undefined ? { focus: objectContext.focus } : {}),
    }),
    [role, plane, permissionKeys, objectContext]
  );

  const llmAugmentation = usePaletteLlmAugmentation({
    tenantId,
    query,
    ctx: resolverCtx,
    enabled: (options.llmAugmentationEnabled ?? true) && query.trim().length > 0,
  });

  const intentProposalResult = usePaletteIntentProposal({
    query,
    ctx: resolverCtx,
    enabled: query.trim().length > 0,
  });

  const mergedCommands = useMemo(
    () =>
      mergePaletteLlmIntoGrouped(
        baseCommands,
        llmAugmentation.commands,
        llmAugmentation.disclosure
      ),
    [baseCommands, llmAugmentation.commands, llmAugmentation.disclosure]
  );

  return {
    ...mergedCommands,
    hasObjectContext: objectContext !== null,
    ...(objectContext?.rootLabel !== undefined ? { rootLabel: objectContext.rootLabel } : {}),
    ...(objectContext?.businessDomain !== undefined
      ? { businessDomain: objectContext.businessDomain }
      : {}),
    ...(objectContext?.object !== undefined ? { commandObject: objectContext.object } : {}),
    tenantId,
    llmLoading: llmAugmentation.loading,
    ...(llmAugmentation.stub !== undefined ? { llmStub: llmAugmentation.stub } : {}),
    ...(intentProposalResult.proposal !== undefined
      ? { intentProposal: intentProposalResult.proposal }
      : {}),
    commandContext: resolverCtx,
  };
}
